import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { SubscriptionStatus } from "@/lib/api/subscriptions";
import { SUBSCRIPTION_STATUS_EVENT } from "@/lib/subscription-status-events";

interface UseSubscriptionValidationReturn {
  subscriptionStatus: SubscriptionStatus | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  isSubscriptionValid: boolean;
  shouldRedirect: boolean;
  redirectPath: string | null;
}

export function useSubscriptionValidation(): UseSubscriptionValidationReturn {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const { user } = useAuth();

  const checkSubscription = useCallback(async () => {
    if (!user?.schoolId) {
      setLoading(false);
      return;
    }

    // Check if user belongs to super admin school
    const superAdminSchoolId = process.env.NEXT_PUBLIC_SUPER_ADMIN_SCHOOL_ID || 'cmljhuey3000sqxxe6ew0btq9';
    const isFromSuperAdminSchool = user.schoolId === superAdminSchoolId;
    
    // Check if user has roles that should be excluded from subscription checks when from super admin school
    const excludedRoles = ['admin', 'student', 'instructor', 'support_admin'];
    const hasExcludedRole = user.roles?.some(role => excludedRoles.includes(role));
    const shouldSkipCheck = isFromSuperAdminSchool && hasExcludedRole;

    // Skip validation for super_admin and support_super_admin roles
    // Also skip for users from super admin school with excluded roles
    if (
      user.roles?.includes("super_admin") ||
      user.roles?.includes("support_super_admin") ||
      shouldSkipCheck
    ) {
      setSubscriptionStatus({
        isValid: true,
        status: "super_admin",
        isExpired: false,
        expiresAt: null,
        gracePeriodEnds: null,
        redirectTo: null,
      });
      setLoading(false);
      return;
    }

    // For admin role, check admin's own subscription
    if (user.roles?.includes("admin")) {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001/api/v1";
        const response = await fetch(`${apiUrl}/admin/subscription-status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSubscriptionStatus(data.data.data);
        } else if (response.status === 403) {
          // Admin subscription expired or invalid
          const errorData = await response.json();
          setSubscriptionStatus(errorData.data);
        } else {
          console.error("Failed to fetch admin subscription status");
          // On error, deny access to ensure security
          setSubscriptionStatus({
            isValid: false,
            status: "error",
            isExpired: true,
            expiresAt: null,
            gracePeriodEnds: null,
            redirectTo: "admin_renewal",
            message: "Unable to verify admin subscription status",
          });
        }
      } catch (error) {
        console.error("Error checking admin subscription status:", error);
        // On error, deny access to ensure security
        setSubscriptionStatus({
          isValid: false,
          status: "error",
          isExpired: true,
          expiresAt: null,
          gracePeriodEnds: null,
          redirectTo: "admin_renewal",
          message: "Unable to verify admin subscription status",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001/api/v1";
      const response = await fetch(`${apiUrl}/subscriptions/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data.data);
      } else if (response.status === 403) {
        // Subscription expired or invalid
        const errorData = await response.json();
        setSubscriptionStatus(errorData.data);
      } else {
        console.error("Failed to fetch subscription status");
        // On error, deny access to ensure security
        setSubscriptionStatus({
          isValid: false,
          status: "error",
          isExpired: true,
          expiresAt: null,
          gracePeriodEnds: null,
          redirectTo: "renewal",
          message: "Unable to verify subscription status",
        });
      }
    } catch (error) {
      console.error("Error checking subscription status:", error);
      // On error, deny access to ensure security
      setSubscriptionStatus({
        isValid: false,
        status: "error",
        isExpired: true,
        expiresAt: null,
        gracePeriodEnds: null,
        redirectTo: "renewal",
        message: "Unable to verify subscription status",
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleSubscriptionStatusUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<SubscriptionStatus>;
      if (!customEvent.detail) return;
      setSubscriptionStatus(customEvent.detail);
      setLoading(false);
    };

    window.addEventListener(SUBSCRIPTION_STATUS_EVENT, handleSubscriptionStatusUpdated);
    return () => {
      window.removeEventListener(SUBSCRIPTION_STATUS_EVENT, handleSubscriptionStatusUpdated);
    };
  }, []);

  // Determine if subscription is valid
  const isSubscriptionValid = subscriptionStatus?.isValid ?? false;

  // Determine if we should redirect
  // Don't redirect if subscription is valid (including grace period)
  const shouldRedirect =
    !isSubscriptionValid &&
    subscriptionStatus !== null &&
    subscriptionStatus?.redirectTo !== null;

  // Determine redirect path
  const redirectPath = (() => {
    if (!user && !shouldRedirect) return null;

    const redirectTo = subscriptionStatus?.redirectTo;
    const isAdmin =
      user?.roles?.includes("admin") || user?.roles?.includes("super_admin");

    /*
    console.log("Redirect path calculation:", {
      shouldRedirect,
      redirectTo,
      isAdmin,
      subscriptionStatus,
    });
    */


    if (redirectTo === "renewal" && isAdmin) {
      return "/subscription/admin-renewal";
    } else if (redirectTo === "admin_renewal") {
      return "/subscription/admin-renewal";
    } else if (redirectTo === "renewal") {
      return "/subscription-expired";
    }

    // Default fallback - if subscription is invalid, redirect based on user role
    if (isAdmin) {
      return "/subscription/admin-renewal";
    } else {
      return "/subscription-expired";
    }
  })();

  // Handle redirection (skip for admin; admin redirects are handled by useAdminSubscriptionGate)
  useEffect(() => {
    if (user?.roles?.includes("admin")) return;
    if (shouldRedirect && redirectPath) {
      // Check if we're already on the target page to prevent infinite redirects
      const currentPath = window.location.pathname;
      /*
      console.log("Subscription redirect check:", {
        shouldRedirect,
        redirectPath,
        currentPath,
        subscriptionStatus,
      });
      */
      // Define allowed paths for invalid subscriptions
      const allowedPaths = [
        "/login",
        "/register",
        "/forgot-password",
        "/subscription-expired",
        "/subscription/admin-renewal",
        "/admin/subscription-renewal",
        "/admin/school-subscriptions/create",
        "/admin/subscription-plans",
        "/admin/payments",
        "/admin/course-payments",
        "/auth/login",
        "/auth/register",
        "/auth/logout",
        "/logout",
        "/auth/forgot-password",
        "/auth/reset-password",
      ];

      const isAllowedPath = allowedPaths.some((path) =>
        currentPath.startsWith(path)
      );

      if (!isAllowedPath) {
        // console.log("Redirecting to:", redirectPath);
        // Use replace instead of push to avoid back button issues
        router.replace(redirectPath);
      }
    }
  }, [user, shouldRedirect, redirectPath, router]);

  return {
    subscriptionStatus,
    loading,
    checkSubscription,
    isSubscriptionValid,
    shouldRedirect,
    redirectPath,
  };
}
